import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Nav, NavLink, NavbarContainer, Span, NavLogo, NavItems, GitHubButton, ThemeButton, ButtonContainer, MobileIcon, MobileMenu, MobileLink, MobileOverlay } from './NavbarStyledComponent';
import { FaBars, FaGithub, FaLinkedin, FaMoon, FaSun, FaTimes } from 'react-icons/fa';
import { Bio } from '../../data/constants';
import ProjectImage8 from '../../images/brand.png';
import { Link, useLocation } from 'react-router-dom';
import { assetUrl, getBasePath } from '../../blog/urls';

const navLinks = [
  { id: 'about', label: 'About' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'experience', label: 'Experience' },
  { id: 'education', label: 'Education' },
  { id: 'research', label: 'Research' },
  { id: 'contact', label: 'Contact' },
  { id: 'blog', label: 'Blog' },
];

const Navbar = ({ darkMode, toggleDarkMode }) => {
  const reducedMotion = useReducedMotion();
  const { pathname } = useLocation();
  const onBlog = pathname !== '/';
  const sectionHref = id => onBlog ? getBasePath() + '/#' + id : '#' + id;
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('about');
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    const updateActiveSection = () => {
      const offset = 150;
      let current = navLinks[0].id;

      navLinks.forEach(({ id }) => {
        const section = document.getElementById(id);
        if (!section) return;
        const top = section.getBoundingClientRect().top;
        if (top <= offset) current = id;
      });

      setActiveSection(current);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);

    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const toggleButton = toggleRef.current;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const focusTimer = setTimeout(() => {
        const el = menuRef.current && menuRef.current.querySelector('a,button');
        if (el) el.focus();
      }, 50);

      const handleKey = (e) => {
        if (e.key === 'Escape') setIsOpen(false);
        if (e.key === 'Tab' && menuRef.current) {
          const focusables = menuRef.current.querySelectorAll('a,button');
          if (!focusables.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKey);
      return () => {
        document.body.style.overflow = prev;
        clearTimeout(focusTimer);
        document.removeEventListener('keydown', handleKey);
        toggleButton?.focus();
      };
    }
  }, [isOpen]);

  return (
    <Nav role="navigation" aria-label="Main Navigation">
      <NavbarContainer>
        <NavLogo href={sectionHref('home')} aria-label="Home - Kisho Jeyapragash">
          <img src={assetUrl(ProjectImage8)} alt="" width={54} height={54} decoding="async" />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.05 }}>
            <Span>Kisho Jeyapragash</Span>
            <div style={{ fontSize: 13, color: 'var(--nav-muted)', marginTop: 4 }}>Software Engineer</div>
          </div>
        </NavLogo>

        <MobileIcon ref={toggleRef} onClick={() => setIsOpen(!isOpen)} aria-controls="mobile-navigation" aria-expanded={isOpen} aria-label="Open navigation">
          <FaBars />
        </MobileIcon>

        <NavItems>
          {navLinks.map(({ id, label }) => (
            <NavLink key={id} as={id === 'blog' ? Link : 'a'} to={id === 'blog' ? '/blog' : undefined} href={id === 'blog' ? undefined : sectionHref(id)} $active={onBlog ? id === 'blog' : activeSection === id} aria-current={(onBlog ? id === 'blog' : activeSection === id) ? 'page' : undefined}>
              {label}
            </NavLink>
          ))}
        </NavItems>

        <ButtonContainer>
          <GitHubButton href={Bio.github} target="_blank" aria-label="GitHub" rel="noopener noreferrer"><FaGithub /></GitHubButton>
          <GitHubButton href={Bio.linkedin} target="_blank" aria-label="LinkedIn" rel="noopener noreferrer"><FaLinkedin /></GitHubButton>
          <ThemeButton
            type="button"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={darkMode}
            onClick={toggleDarkMode}
            title={darkMode ? 'Light mode' : 'Dark mode'}
          >
            {darkMode ? <FaSun /> : <FaMoon />}
          </ThemeButton>
        </ButtonContainer>

        {isOpen && createPortal(
          <MobileOverlay onClick={event=>{if(event.target===event.currentTarget)setIsOpen(false);}}>
          <MobileMenu as={motion.div} initial={reducedMotion ? false : { opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reducedMotion ? 0 : 0.2 }} id="mobile-navigation" isOpen={isOpen} ref={menuRef} role="dialog" aria-modal="true" aria-label="Mobile navigation">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <h2 style={{fontSize:20,alignSelf:'center',marginRight:'auto'}}>Navigation</h2>
              <ThemeButton type="button" onClick={toggleDarkMode} aria-pressed={darkMode} aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>{darkMode ? <FaSun /> : <FaMoon />}</ThemeButton>
              <ThemeButton type="button" onClick={() => setIsOpen(false)} aria-label="Close navigation"><FaTimes /></ThemeButton>
            </div>
            {navLinks.map(({ id, label }) => (
              <MobileLink key={id} as={id === 'blog' ? Link : 'a'} to={id === 'blog' ? '/blog' : undefined} href={id === 'blog' ? undefined : sectionHref(id)} onClick={() => setIsOpen(false)} aria-current={(onBlog ? id === 'blog' : activeSection === id) ? 'page' : undefined}>{label}</MobileLink>
            ))}
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <GitHubButton href={Bio.github} target="_blank" aria-label="GitHub" rel="noopener noreferrer"><FaGithub /></GitHubButton>
              <GitHubButton href={Bio.linkedin} target="_blank" aria-label="LinkedIn" rel="noopener noreferrer"><FaLinkedin /></GitHubButton>
            </div>
          </MobileMenu>
          </MobileOverlay>
        , document.body)}
      </NavbarContainer>
    </Nav>
  );
};

export default Navbar;




